import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";


gsap.registerPlugin(ScrollTrigger);


export const LampContainer = ({
                                  children,
                                  className,
                              }: {
    children: React.ReactNode;
    className?: string;
}) => {
    // Refs for every previously-motion animated element
    const leftGlow = useRef<HTMLDivElement>(null);
    const rightGlow = useRef<HTMLDivElement>(null);
    const midGlow = useRef<HTMLDivElement>(null);
    const midLine = useRef<HTMLDivElement>(null);
    const title = useRef<HTMLHeadingElement>(null);

    useLayoutEffect(() => {
        // Animate title
        gsap.fromTo(
            title.current,
            { opacity: 0.5, y: 100 },
            {
                animationDelay: 6000,
                opacity: 1,
                y: 0,
                duration: 0.8,
                delay: 0.3,
                ease: "power1.inOut",
                scrollTrigger: {
                    trigger: title.current,
                    start: "top 90%",
                },
            }
        );

        // Shared animation config for glow blocks
        const glowAnim = (el: HTMLDivElement | null) => {
            if (!el) return;
            gsap.fromTo(
                el,
                { opacity: 0.5, width: "15rem" },
                {
                    opacity: 1,
                    width: "30rem",
                    duration: 0.8,
                    delay: 0.3,
                    ease: "power1.inOut",
                    scrollTrigger: {
                        trigger: el,
                        start: "top 85%",
                    },
                }
            );
        };

        glowAnim(leftGlow.current);
        glowAnim(rightGlow.current);

        // Middle glow width animation
        if (midGlow.current) {
            gsap.fromTo(
                midGlow.current,
                { width: "8rem" },
                {
                    width: "16rem",
                    duration: 0.8,
                    delay: 0.3,
                    ease: "power1.inOut",
                    scrollTrigger: { trigger: midGlow.current, start: "top 85%" },
                }
            );
        }

        // Middle line
        if (midLine.current) {
            gsap.fromTo(
                midLine.current,
                { width: "15rem" },
                {
                    width: "30rem",
                    duration: 0.8,
                    delay: 0.3,
                    ease: "power1.inOut",
                    scrollTrigger: { trigger: midLine.current, start: "top 85%" },
                }
            );
        }
    }, []);

    return (
        <div
            className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#251c5b] w-full  z-0">
            <div className="relative flex w-full flex-1 scale-y-125 items-center justify-center isolate z-0 ">
                {/* LEFT GLOW */}
                <div
                    ref={leftGlow}
                    style={{
                        backgroundImage: `conic-gradient(var(--conic-position), var(--tw-gradient-stops))`,
                    }}
                    className="absolute inset-auto right-1/2 h-56 overflow-visible w-[30rem] bg-gradient-conic from-[#a574bf] via-transparent to-transparent text-white [--conic-position:from_70deg_at_center_top]"
                >
                    <div className="absolute w-[100%] left-0 bg-[#251c5b] h-40 bottom-0 z-20 [mask-image:linear-gradient(to_top,white,transparent)]" />
                    <div className="absolute w-40 h-[100%] left-0 bg-[#251c5b] bottom-0 z-20 [mask-image:linear-gradient(to_right,white,transparent)]" />
                </div>

                {/* RIGHT GLOW */}
                <div
                    ref={rightGlow}
                    style={{
                        backgroundImage: `conic-gradient(var(--conic-position), var(--tw-gradient-stops))`,
                    }}
                    className="absolute inset-auto left-1/2 h-56 w-[30rem] bg-gradient-conic from-transparent via-transparent to-[#a574bf] text-white [--conic-position:from_290deg_at_center_top]"
                >
                    <div className="absolute w-40 h-[100%] right-0 bg-[#251c5b] bottom-0 z-20 [mask-image:linear-gradient(to_left,white,transparent)]" />
                    <div className="absolute w-[100%] right-0 bg-[#251c5b] h-40 bottom-0 z-20 [mask-image:linear-gradient(to_top,white,transparent)]" />
                </div>

                <div className="absolute top-1/2 h-48 w-full translate-y-12 scale-x-150 bg-[#251c5b] blur-2xl"></div>
                <div className="absolute top-1/2 z-50 h-48 w-full bg-transparent opacity-10 backdrop-blur-md"></div>
                <div className="absolute inset-auto z-50 h-36 w-[28rem] -translate-y-1/2 rounded-full bg-[#a574bf] opacity-50 blur-3xl"></div>

                {/* CENTER GLOW */}
                <div
                    ref={midGlow}
                    className="absolute inset-auto z-30 h-36 w-64 -translate-y-[6rem] rounded-full bg-[#a574bf] blur-2xl"
                ></div>

                {/* CENTER LINE */}
                <div
                    ref={midLine}
                    className="absolute inset-auto z-50 h-0.5 w-[30rem] -translate-y-[7rem] bg-[#a574bf]"
                ></div>

                <div className="absolute inset-auto z-40 h-44 w-full -translate-y-[12.5rem] bg-[#251c5b]"></div>
            </div>

            <div className="relative z-50 flex -translate-y-80 flex-col items-center px-5">
                <h1 ref={title}>{children}</h1>
            </div>
        </div>
    );
};
